import { Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { DmAvatar, DmButton, DmFileDropZone, DmIcon } from '@digitale-menschen/component-library-angular';
import { notify } from '../../../utilities/notify.utility';

/** What the picture route accepts, as the API reports it. */
export interface ProfilbildLimits {
  readonly erlaubteMimetypen: readonly string[];
  readonly maxDateigroesseBytes: number;
}

/**
 * The «Benutzerfoto» control of a user form: the current picture beside a drop zone.
 *
 * **Nothing is uploaded here.** A pick is staged and previewed, and the form applies it in its
 * save — the only way it can work on a blank form, where there is no user to attach a picture to
 * until the create returns an id, and the same save semantics as every other field for an edit:
 * closing without saving leaves the picture as it was. The form reads {@link stagedFile} and
 * {@link removeRequested} and calls {@link reset} once it has written them.
 *
 * `accept` and `maxsize` come from the API's own limits, so the zone cannot refuse what the route
 * would take or take what it refuses; the hint is derived from the same values.
 */
@Component({
  selector: 'lp-mein-profil-profilbild',
  imports: [DmAvatar, DmButton, DmFileDropZone, DmIcon],
  templateUrl: './mein-profil-profilbild.component.html',
  host: { class: 'dm-block dm-container' },
})
export class MeinProfilProfilbildComponent {
  private readonly destroyRef = inject(DestroyRef);

  /** The stored picture as an absolute URL; `null` when the user has none or is new. */
  public readonly savedUrl = input<string | null>(null);

  /** What the avatar falls back to while there is no picture. */
  public readonly initials = input<string>('');

  /** What the route accepts. Until it is known the zone checks nothing, and the API still does. */
  public readonly limits = input<ProfilbildLimits | undefined>(undefined);

  public readonly disabled = input<boolean>(false);

  /** A picked file waiting for the form's save, together with the `blob:` URL previewing it. */
  private readonly staged = signal<{ file: File; objectUrl: string } | null>(null);

  /** Set when the user asked for the stored picture to go, pending the save. */
  public readonly removeRequested = signal<boolean>(false);

  public readonly stagedFile = computed<File | null>(() => this.staged()?.file ?? null);

  /** Whether there is anything for the form to write. */
  public readonly hasChanges = computed<boolean>(() => this.staged() !== null || this.removeRequested());

  protected readonly maxBytes = computed<number | undefined>(() => this.limits()?.maxDateigroesseBytes);

  /** What saving would leave behind: the picked file, else the stored picture unless removed. */
  protected readonly previewUrl = computed<string | null>(() => {
    const staged = this.staged();
    if (staged) {
      return staged.objectUrl;
    }
    return this.removeRequested() ? null : this.savedUrl();
  });

  protected readonly accept = computed<string>(() => (this.limits()?.erlaubteMimetypen ?? []).join(','));

  /** The limit in whole megabytes, or `undefined` until the API has said what it is. */
  private readonly maxMb = computed<number | undefined>(() => {
    const bytes = this.maxBytes();
    return bytes === undefined ? undefined : Math.round(bytes / 1024 / 1024);
  });

  /** The drop zone's hint once the limit is known; empty rather than a made-up «max. 0 MB» before. */
  protected readonly hint = computed<string>(() => {
    const maxMb = this.maxMb();
    return maxMb === undefined ? '' : $localize`:@@administration.benutzer.profilbild.hint:(max. ${maxMb}:groesse: MB)`;
  });

  protected readonly tooLargeMessage = computed<string>(() => {
    const maxMb = this.maxMb();
    return maxMb === undefined
      ? ''
      : $localize`:@@benutzerverwaltung.form.benutzerfotoZuGross:Das Bild ist zu gross – maximal ${maxMb}:groesse: MB.`;
  });

  protected readonly wrongTypeMessage = $localize`:@@benutzerverwaltung.form.benutzerfotoTypUngueltig:Dieses Dateiformat wird nicht unterstützt.`;
  protected readonly avatarLabel = $localize`:@@benutzerverwaltung.form.benutzerfoto:Benutzerfoto`;

  constructor() {
    // A `blob:` URL keeps its file alive until it is revoked.
    this.destroyRef.onDestroy(() => this.discardStaged());
  }

  /** Forgets any pending pick or removal — after the form saved, or loaded another user. */
  public reset(): void {
    this.discardStaged();
    this.removeRequested.set(false);
  }

  /**
   * Stages a picked or dropped image.
   *
   * @param {File[]} files - What the zone accepted; single-file, so only the first is used
   */
  protected onPicked(files: File[]): void {
    const file = files[0];
    if (!file) {
      return;
    }
    this.discardStaged();
    this.staged.set({ file, objectUrl: URL.createObjectURL(file) });
    this.removeRequested.set(false);
  }

  /**
   * Reports a file the zone refused. Its own inline message sits inside the zone and is easy to
   * miss beside a preview that did not change; the toast makes «nothing happened» legible.
   *
   * @param {string} reason - The already-translated reason
   */
  protected onRejected(reason: string): void {
    notify.error(reason);
  }

  /**
   * Drops the picture, pending the save. Discarding a pick only goes back to the stored picture,
   * which a second click then removes; only a stored one needs a request.
   */
  protected onRemove(): void {
    if (this.staged()) {
      this.discardStaged();
      return;
    }
    this.removeRequested.set(this.savedUrl() !== null);
  }

  /** Releases the `blob:` URL of a staged file, if there is one. */
  private discardStaged(): void {
    const staged = this.staged();
    if (staged) {
      URL.revokeObjectURL(staged.objectUrl);
      this.staged.set(null);
    }
  }
}
